import { HttpService } from "data/http-service";
import {
  CreateTechnique,
  Technique,
  TechniquesFilters,
  UpdateSequences,
} from "domain/entities/techniques.entities";
import {
  DurationFilter,
  ActivitiesPageFilters,
  ActivityAdvice,
} from "domain/entities/activities.entities";
import { TechniquesGateway } from "domain/techniques/techniques.gateway";
import {
  techniqueDtoSchema,
  techniquesDtoSchema,
} from "data/techniques/techniques.dto.schemas";
import { Snackbar } from "ui/pages/snackbar";
import { translate } from "ui/i18n";
import { AxiosError } from "axios";
import { getActivitiesFilters } from "data/activities/get-activities-filters";
import { adviceDtoSchema } from "data/activities/activities.dto.schemas";

export class TechniquesHttpGateway implements TechniquesGateway {
  async getTechniques(
    filters: TechniquesFilters &
      Partial<ActivitiesPageFilters> &
      Partial<{ duration: DurationFilter }>
  ): Promise<{
    techniques: Technique[];
    totalCount: number;
    success: boolean;
  }> {
    return HttpService.get("techniques", {
      params: getActivitiesFilters(filters),
    })
      .then((response) => {
        if (response.status === 200) {
          const parsedTechniques = techniquesDtoSchema.safeParse(
            response.data.data
          );
          if (parsedTechniques.success) {
            return {
              techniques: parsedTechniques.data,
              totalCount: response.data.total,
              success: true,
            };
          } else return { techniques: [], totalCount: 0, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Techniques.FindError"),
          });
          return {
            techniques: [],
            totalCount: 0,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Techniques.FindError"),
        });
        return { techniques: [] as Technique[], totalCount: 0, success: false };
      });
  }

  async getTechnique({
    id,
    consignes,
  }: {
    id: number;
    consignes?: boolean;
  }): Promise<{
    technique: Technique | null;
    success: boolean;
  }> {
    return HttpService.get(
      `techniques/${id}${consignes ? `?$consignes=${consignes}` : ""}`
    )
      .then((response) => {
        if (response.status === 200) {
          const parsedTechnique = techniqueDtoSchema.safeParse(response.data);
          if (parsedTechnique.success) {
            return {
              technique: parsedTechnique.data,
              success: true,
            };
          } else return { technique: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Technique.FindError"),
          });
          return {
            technique: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Technique.FindError"),
        });
        return { technique: null as Technique, success: false };
      });
  }

  async getTechniqueAdvice(id: number): Promise<{
    advice: ActivityAdvice | null;
    success: boolean;
  }> {
    return HttpService.get(`conseils/random`, { params: { techniqueId: id } })
      .then((response) => {
        if (response.status === 200) {
          const parsedAdvice = adviceDtoSchema.safeParse(response.data);
          if (parsedAdvice.success) {
            return {
              advice: parsedAdvice.data,
              success: true,
            };
          } else return { advice: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Advice.FindError"),
          });
          return {
            advice: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Advice.FindError"),
        });
        return { advice: null as ActivityAdvice, success: false };
      });
  }

  async getFavoritesTechniques(techniqueIds: number[]): Promise<{
    techniques: Technique[];
    success: boolean;
  }> {
    const ids = techniqueIds.length > 0 ? techniqueIds : [-1];
    return HttpService.get("techniques", { params: { "id[$in]": ids } })
      .then((response) => {
        if (response.status === 200) {
          const parsedTechniques = techniquesDtoSchema.safeParse(
            response.data.data
          );
          if (parsedTechniques.success) {
            return {
              techniques: parsedTechniques.data,
              success: true,
            };
          } else return { techniques: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Techniques.FindError"),
          });
          return {
            techniques: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Techniques.FindError"),
        });
        return { techniques: [] as Technique[], success: false };
      });
  }

  async createTechnique(technique: CreateTechnique): Promise<{
    success: boolean;
    technique: Technique | null;
  }> {
    return HttpService.post(`user-techniques`, technique)
      .then((response) => {
        if (response.status === 201 && response.data.id) {
          return {
            technique: response.data as Technique,
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Technique.CreationError"),
          });
          return {
            technique: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Technique.CreationError"),
        });
        return { technique: null as Technique, success: false };
      });
  }

  async updateSequences(body: UpdateSequences): Promise<{
    success: boolean;
  }> {
    return HttpService.patch(`user-sequences/-1`, body)
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Technique.UpdateSequencesError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Technique.UpdateSequencesError"),
        });
        return { success: false };
      });
  }

  async getRandomTechnique(): Promise<{
    technique: Technique | null;
    success: boolean;
  }> {
    return HttpService.get("techniques/random")
      .then((response) => {
        if (response.status === 200) {
          const parsedTechnique = techniqueDtoSchema.safeParse(response.data);
          if (parsedTechnique.success) {
            return {
              technique: parsedTechnique.data,
              success: true,
            };
          } else return { technique: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Technique.FindError"),
          });
          return {
            technique: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Technique.FindError"),
        });
        return { technique: null as Technique, success: false };
      });
  }
}
